<template>
        <!-- Only show FlowNavigation for combination packages, not single packages -->
        <div v-if="isComboPackage" class="flow-navigation bg-white border-b border-[var(--bone)]">
                <div class="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
                        <!-- Show instructional badge when on Index with no cart, or other pages with no cart -->
                        <div v-if="!hasCartItems && currentStep === 'Index'" class="flex items-center justify-center">
                                <div class="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl text-center">
                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        <span class="text-xs sm:text-sm font-medium text-[var(--charcoal-dark)]">{{ instructionalText }}</span>
                                </div>
                        </div>

                        <!-- Show instructional text with My Trip button on Index when combo has cart -->
                        <div v-else-if="showMyTripButton" class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-4">
                                <div class="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl text-center">
                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                        </svg>
                                        <span class="text-xs sm:text-sm font-medium text-[var(--charcoal-dark)]">{{ instructionalText }}</span>
                                </div>
                                <button
                                        @click="goToMyTrip"
                                        type="button"
                                        class="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all bg-[var(--charcoal)] hover:bg-[var(--charcoal-dark)] text-white shadow-md hover:shadow-lg sm:ml-auto"
                                >
                                        <span class="text-sm sm:text-base whitespace-nowrap">My Trip</span>
                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                </button>
                        </div>

                        <!-- Show navigation buttons (controlled by showButtons computed) -->
                        <div v-else-if="showButtons" class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-4">
                                <!-- Previous Button -->
                                <button
                                        v-if="previousConfig"
                                        @click="handlePrevious"
                                        type="button"
                                        class="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-[var(--charcoal-dark)] order-2 sm:order-1"
                                >
                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                        <span class="text-sm sm:text-base whitespace-nowrap">{{ previousConfig.text }}</span>
                                </button>
                                <div v-else class="hidden sm:block flex-shrink-0"></div>

                                <!-- Next Button or My Trip Button -->
                                <button
                                        v-if="nextConfig || showMyTripButton"
                                        @click="handleButtonClick"
                                        type="button"
                                        class="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all bg-[var(--charcoal)] hover:bg-[var(--charcoal-dark)] text-white shadow-md hover:shadow-lg order-1 sm:order-2 sm:ml-auto"
                                >
                                        <span class="text-sm sm:text-base whitespace-nowrap">{{ nextConfig ? nextConfig.text : 'My Trip' }}</span>
                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                </button>
                        </div>

                        <!-- Instructional text for Index page -->
                        <div v-else-if="currentStep === 'Index'" class="flex items-center justify-center">
                                <div class="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl text-center">
                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                        </svg>
                                        <span class="text-xs sm:text-sm font-medium text-[var(--charcoal-dark)]">{{ instructionalText }}</span>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script>
export default {
        name: 'FlowNavigation',
        props: {
                currentStep: {
                        type: String,
                        required: true,
                        validator: value => ['Index', 'Init', 'AddonsNew', 'MyTrip', 'Maps', 'Checkout'].includes(value)
                },
                formId: {
                        type: String,
                        default: null
                }
        },
        computed: {
                isComboPackage() {
                        return this.$store.state.comboIds && this.$store.state.comboIds !== 0;
                },
                hasCartItems() {
                        const cartItem = this.$store.state.cartItem || {};
                        return Object.keys(cartItem).length > 0;
                },
                hasCustomFields() {
                        return this.$store.state.hasCustomFields;
                },
                showButtons() {
                        // Index page never shows regular buttons
                        if (this.currentStep === 'Index') {
                                return false;
                        }
                        
                        // Init and AddonsNew always show buttons (needed for form submission)
                        if (this.currentStep === 'Init' || this.currentStep === 'AddonsNew') {
                                return true;
                        }
                        
                        // Other pages only show buttons if cart has items
                        return this.hasCartItems;
                },
                showMyTripButton() {
                        // Show My Trip button only on Index page when combo package has cart items
                        return this.currentStep === 'Index' && this.isComboPackage && this.hasCartItems;
                },
                instructionalText() {
                        // For Index page, show different messages based on cart status
                        if (this.currentStep === 'Index') {
                                return this.hasCartItems 
                                        ? 'Choose another tour or edit your selections below'
                                        : 'Select a tour to get started';
                        }
                        
                        const texts = {
                                'Init': 'Schedule your first tour to continue',
                                'AddonsNew': 'Complete your selections',
                                'MyTrip': 'Add at least one tour',
                                'Maps': 'Schedule tours to view map',
                                'Checkout': 'Your cart is empty'
                        };
                        return texts[this.currentStep] || 'Select a tour to continue';
                },
                previousConfig() {
                        const configs = {
                                'Init': { text: 'Back to Tours', route: 'Index' },
                                'AddonsNew': { text: 'Back to Schedule', route: 'Init' },
                                'MyTrip': { text: 'Browse Tours', route: 'Index' },
                                'Maps': { text: 'My Trip', route: 'MyTrip' },
                                'Checkout': { text: 'View Map', route: this.isComboPackage ? 'Maps' : 'Addons' }
                        };
                        return configs[this.currentStep] || null;
                },
                nextConfig() {
                        const configs = {
                                'Init': {
                                        // For combo packages: goes to Addons (if available) or Index (if no addons)
                                        // For single packages: goes to Addons (if available) or Checkout
                                        text: this.hasCustomFields 
                                                ? 'Continue to Add-ons' 
                                                : (this.isComboPackage ? 'Browse Tours' : 'Continue to Checkout'),
                                        isSubmit: true,
                                        formId: this.formId || 'tour-schedule-form'
                                },
                                'AddonsNew': {
                                        // For combo packages: goes to Index (Browse Tours)
                                        // For single packages: goes to Checkout
                                        text: this.isComboPackage ? 'Browse Tours' : 'Continue to Checkout',
                                        isSubmit: true,
                                        formId: this.formId || 'addons-form'
                                },
                                'MyTrip': {
                                        text: 'View Map',
                                        route: 'Maps',
                                        isSubmit: false
                                },
                                'Maps': {
                                        text: 'Checkout',
                                        route: 'Checkout',
                                        isSubmit: false
                                }
                        };
                        return configs[this.currentStep] || null;
                }
        },
        methods: {
                handlePrevious() {
                        if (this.previousConfig && this.previousConfig.route) {
                                // Set mindChange=1 when navigating to Index to prevent auto-redirect
                                if (this.previousConfig.route === 'Index') {
                                        this.$store.dispatch('storeMindChange', 1);
                                }
                                this.$router.push({ name: this.previousConfig.route });
                        }
                },
                handleNext() {
                        if (this.nextConfig) {
                                if (this.nextConfig.isSubmit) {
                                        // Emit event for parent to handle form submission
                                        this.$emit('submit');
                                } else if (this.nextConfig.route) {
                                        this.$router.push({ name: this.nextConfig.route });
                                }
                        }
                },
                handleButtonClick() {
                        if (this.showMyTripButton) {
                                this.goToMyTrip();
                        } else if (this.nextConfig) {
                                this.handleNext();
                        }
                },
                goToMyTrip() {
                        this.$store.dispatch('storeMindChange', 1);
                        this.$router.push({ name: 'MyTrip' });
                }
        }
};
</script>

<style scoped>
.flow-navigation {
        /* Solid white background for clean appearance when scrolling */
        background: #ffffff;
}
</style>
